<template>
  <v-container fluid class="admin-container pa-0">
    <!-- Modern Header with Gradient Background -->
    <div class="hero-section">
      <div class="hero-background"></div>
      <v-container>
        <v-row align="center" justify="space-between" class="hero-content">
          <v-col cols="12" lg="8">
            <div class="hero-text">
              <h1 class="top-title display-1 font-weight-bold text-white mb-3">
                Admin Management
                <v-icon large class="ml-2 hero-icon" color="#ffff">mdi-shield-crown</v-icon>
              </h1>
              <p class="top-sub-title text-h6 text-white hero-subtitle">
                Streamline your team with intelligent administrator controls
              </p>
            </div>
          </v-col>
          <v-col cols="12" lg="4" class="text-lg-right text-center">
            <v-btn
              x-large
              color="white"
              class="create-btn elevation-8"
              @click="openCreateDialog"
              rounded
            >
              <v-icon left size="24">mdi-plus-circle-outline</v-icon>
              Create Admin
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <v-container class="content-container">
      <!-- Modern Stats Dashboard -->
      <v-row class="stats-section mb-8" v-show="false">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card total-admins" elevation="0">
            <v-card-text class="pa-6">
              <div class="d-flex align-center">
                <div class="stat-icon-wrapper mr-4">
                  <v-icon size="32" class="stat-icon">mdi-account-group</v-icon>
                </div>
                <div class="flex-grow-1">
                  <div class="stat-number">{{ admins.length }}</div>
                  <div class="stat-label">Total Admins</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card active-admins" elevation="0">
            <v-card-text class="pa-6">
              <div class="d-flex align-center">
                <div class="stat-icon-wrapper mr-4">
                  <v-icon size="32" class="stat-icon">mdi-account-check</v-icon>
                </div>
                <div class="flex-grow-1">
                  <div class="stat-number">{{ activeAdmins }}</div>
                  <div class="stat-label">Active</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card recent-activity" elevation="0">
            <v-card-text class="pa-6">
              <div class="d-flex align-center">
                <div class="stat-icon-wrapper mr-4">
                  <v-icon size="32" class="stat-icon">mdi-clock-outline</v-icon>
                </div>
                <div class="flex-grow-1">
                  <div class="stat-number">24h</div>
                  <div class="stat-label">Last Activity</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card permissions" elevation="0">
            <v-card-text class="pa-6">
              <div class="d-flex align-center">
                <div class="stat-icon-wrapper mr-4">
                  <v-icon size="32" class="stat-icon">mdi-security</v-icon>
                </div>
                <div class="flex-grow-1">
                  <div class="stat-number">99%</div>
                  <div class="stat-label">Security</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Modern Search and Filters -->
      <v-card class="search-card mb-8" elevation="0" v-show="false">
        <v-card-text class="pa-6">
          <v-row align="center" class="search-row">
            <v-col cols="12" md="6" lg="4">
              <div class="search-wrapper">
                <v-text-field
                  v-model="search"
                  placeholder="Search administrators..."
                  prepend-inner-icon="mdi-magnify"
                  solo
                  flat
                  dense
                  hide-details
                  class="search-field"
                  clearable
                ></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" md="6" lg="4">
              <div class="filter-wrapper">
                <v-select
                  v-model="statusFilter"
                  :items="statusOptions"
                  placeholder="Filter by status"
                  prepend-inner-icon="mdi-filter-variant"
                  solo
                  flat
                  dense
                  hide-details
                  clearable
                  class="filter-select"
                ></v-select>
              </div>
            </v-col>
            <v-col cols="12" lg="4" class="text-lg-right">
              <div class="action-buttons">
                <v-btn
                  color="primary"
                  class="mr-2 action-btn"
                  @click="refreshData"
                  :loading="loading"
                  outlined
                  rounded
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
               
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Modern Data Table -->
      <v-card class="table-card" elevation="0">
        <div class="table-header">
          <v-card-title class="table-title">
            <v-icon left size="24" class="title-icon">mdi-account-supervisor</v-icon>
            Administrators
            <v-spacer></v-spacer>
            <v-chip class="admin-count-chip" color="primary" outlined small>
              {{ filteredAdmins.length }} {{ filteredAdmins.length === 1 ? 'Admin' : 'Admins' }}
            </v-chip>
          </v-card-title>
        </div>

        <v-data-table
          :headers="headers"
          :items="filteredAdmins"
          :search="search"
          item-key="_id"
          :loading="loading"
          loading-text="Loading administrators..."
          no-data-text="No administrators found"
          no-results-text="No matching administrators found"
          class="modern-table elevation-0"
          :items-per-page="10"
          :footer-props="{
            'items-per-page-options': [5, 10, 25, 50],
            'items-per-page-text': 'Admins per page:'
          }"
        >
          <template v-slot:loading>
            <div class="loading-container">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              ></v-progress-circular>
              <p class="loading-text mt-4">Loading administrators...</p>
            </div>
          </template>

          <template v-slot:item.thumbnail="{ item }">
            <div class="avatar-container">
              <v-avatar size="48" class="admin-avatar">
                <v-img
                  :src="item.thumbnail"
                  :alt="`${item.first_name} ${item.last_name}`"
                  class="avatar-image"
                >
                  <template v-slot:placeholder>
                    <div class="avatar-placeholder">
                      <v-icon size="24" color="white">mdi-account</v-icon>
                    </div>
                  </template>
                </v-img>
                <div class="status-indicator" :class="item.status || 'active'"></div>
              </v-avatar>
            </div>
          </template>

          <template v-slot:item.name="{ item }">
            <div class="name-cell">
              <div class="admin-name">{{ item.first_name }} {{ item.last_name }}</div>
              <div class="admin-role">Administrator</div>
            </div>
          </template>

          <template v-slot:item.email="{ item }">
            <div class="contact-cell">
              <div class="contact-item">
                <v-icon small class="contact-icon">mdi-email-outline</v-icon>
                <span class="contact-text">{{ item.email }}</span>
              </div>
            </div>
          </template>

          <template v-slot:item.phone="{ item }">
            <div class="contact-cell">
              <div class="contact-item">
                <v-icon small class="contact-icon">mdi-phone-outline</v-icon>
                <span class="contact-text">{{ item.phone || "Not provided" }}</span>
              </div>
            </div>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              small
              :color="getStatusColor(item.status || 'active')"
              :text-color="getStatusTextColor(item.status || 'active')"
              class="status-chip"
            >
              <v-icon small left :icon="getStatusIcon(item.status || 'active')"></v-icon>
              {{ (item.status || 'active').charAt(0).toUpperCase() + (item.status || 'active').slice(1) }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="action-buttons-cell">
              <v-btn
                icon
                small
                class="action-btn-table edit-btn"
                @click="openEditDialog(item)"
              >
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
              
              <v-btn
                icon
                small
                class="action-btn-table delete-btn"
                @click="deleteDialogAdmin(item._id)"
              >
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <!-- Modern Create/Edit Dialog -->
    <div class=""></div>
    <admin-create-dialog
      :dialog="createDialog"
      :onClose="onCloseDialog"
      v-if="createDialog"
      :isEdit="isEdit"
      :adminData="formData"
      @created="getAllAdmins"
      @updated="getAllAdmins"
    />

    <!-- Modern Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="480px" persistent>
      <v-card class="delete-dialog" elevation="24">
        <div class="delete-header">
          <div class="delete-icon-wrapper">
            <v-icon size="48" color="error">mdi-alert-circle-outline</v-icon>
          </div>
          <h2 class="delete-title">Confirm Deletion</h2>
          <p class="delete-subtitle">This action cannot be undone</p>
        </div>
        
        <v-card-text class="delete-content">
          <p class="delete-message">
            Are you sure you want to permanently delete this administrator? 
            All associated data and permissions will be removed.
          </p>
        </v-card-text>
        
        <v-card-actions class="delete-actions pa-6">
          <v-btn
            large
            outlined
            @click="deleteDialog = false"
            class="cancel-btn"
          >
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            large
            color="error"
            class="delete-confirm-btn"
            @click="confirmDelete(adminToDelete)"
            :loading="deleting"
          >
            Delete Admin
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AdminCreateDialog from "../admin/AdminCreateDialog.vue";

export default {
  components: {
    AdminCreateDialog,
  },
  data() {
    return {
      loading: false,
      deleting: false,
      createDialog: false,
      deleteDialog: false,
      isEdit: false,
      formData: {},
      search: "",
      statusFilter: null,
      adminToDelete: null,
      statusOptions: [
        { text: 'All Status', value: null },
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
        { text: 'Pending', value: 'pending' },
      ],
      headers: [
        { text: "", value: "thumbnail", sortable: false, width: "80px" },
        { text: "Name", value: "name", sortable: true, width: "200px" },
        { text: "Email", value: "email", sortable: true },
        { text: "Phone", value: "phone", sortable: true },
        { text: "Status", value: "status", sortable: true, width: "120px" },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          align: "center",
          width: "140px",
        },
      ],
    };
  },
  computed: {
    ...mapGetters("adminDashboard", {
      admins: "fetchAllAdmins",
    }),
    filteredAdmins() {
      let filtered = this.admins;
      if (this.statusFilter) {
        filtered = filtered.filter(admin => 
          (admin.status || 'active') === this.statusFilter
        );
      }
      return filtered;
    },
    activeAdmins() {
      return this.admins.filter(admin => 
        (admin.status || 'active') === 'active'
      ).length;
    }
  },
  methods: {
    ...mapActions("adminDashboard", ["getAllAdmins", "deleteAdmin"]),

    fetchAdmins() {
      this.loading = true;
      this.getAllAdmins().finally(() => {
        this.loading = false;
      });
    },

    refreshData() {
      this.fetchAdmins();
    },

    exportData() {
      // Implementation for export functionality
      console.log('Exporting data...');
    },

    openCreateDialog() {
      this.isEdit = false;
      this.formData = {};
      this.createDialog = true;
    },

    openEditDialog(admin) {
      this.isEdit = true;
      this.formData = { ...admin };
      this.createDialog = true;
    },

    onCloseDialog() {
      this.createDialog = false;
      this.getAllAdmins();
      this.formData = {};
      this.isEdit = false;
    },

    deleteDialogAdmin(id) {
      this.adminToDelete = id;
      this.deleteDialog = true;
    },

    confirmDelete(id) {
      if (!id) return;
      this.deleting = true;
      const payload = { id };
      this.deleteAdmin(payload)
        .then(() => {
          this.deleteDialog = false;
          this.adminToDelete = null;
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.deleting = false;
        });
    },

    viewDetails(item) {
      console.log('View details for:', item);
    },

    resetPassword(item) {
      console.log('Reset password for:', item);
    },

    getStatusColor(status) {
      const colors = {
        active: '#4CAF50',
        inactive: '#9E9E9E',
        pending: '#FF9800',
      };
      return colors[status] || colors.active;
    },

    getStatusTextColor() {
      return 'white';
    },

    getStatusIcon(status) {
      const icons = {
        active: 'mdi-check-circle',
        inactive: 'mdi-pause-circle',
        pending: 'mdi-clock-outline',
      };
      return icons[status] || icons.active;
    }
  },
  mounted() {
    this.fetchAdmins();
  },
};
</script>

<style scoped>
/* Container & Layout */
.admin-container {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}

.content-container {
  max-width: 1400px;
  margin-top: -80px;
  position: relative;
  z-index: 2;
}

/* Hero Section */
.hero-section {
  position: relative;
  padding: 120px 0 160px;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #084468 0%, #1da1f2 50%, #3b7ca1 100%);
  color: #ffff;
}

.hero-background::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.1;
}

.hero-content {
  position: relative;
  z-index: 1;
}

.hero-text {
  animation: slideInLeft 0.8s ease-out;
}

.hero-icon {
  animation: bounce 2s infinite;
}

.hero-subtitle {
  opacity: 0.9;
  font-weight: 300;
}

.create-btn {
  background: linear-gradient(45deg, #ffffff, #f8f9fa) !important;
  color: #667eea !important;
  font-weight: 600;
  letter-spacing: 0.5px;
  transform: translateY(0);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideInRight 0.8s ease-out;
}

.create-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3) !important;
}

/* Stats Section */
.stats-section {
  margin-top: -40px;
  position: relative;
  z-index: 3;
}

.stat-card {
  background: white;
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  transition: all 0.3s ease;
}

.stat-card.total-admins::before {
  background: linear-gradient(90deg, #667eea, #764ba2);
}

.stat-card.active-admins::before {
  background: linear-gradient(90deg, #4CAF50, #45a049);
}

.stat-card.recent-activity::before {
  background: linear-gradient(90deg, #FF9800, #f57c00);
}

.stat-card.permissions::before {
  background: linear-gradient(90deg, #9C27B0, #7B1FA2);
}

.stat-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.stat-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.total-admins .stat-icon-wrapper {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.active-admins .stat-icon-wrapper {
  background: linear-gradient(135deg, #4CAF50, #45a049);
}

.recent-activity .stat-icon-wrapper {
  background: linear-gradient(135deg, #FF9800, #f57c00);
}

.permissions .stat-icon-wrapper {
  background: linear-gradient(135deg, #9C27B0, #7B1FA2);
}

.stat-icon {
  color: white;
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
}

.stat-label {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
  margin-top: 4px;
}

/* Search Card */
.search-card {
  background: white;
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(10px);
}

.search-row {
  gap: 16px;
}

.search-field,
.filter-select {
  border-radius: 12px;
}

.search-field >>> .v-input__control {
  background: #f8f9fa;
  border-radius: 12px;
}

.filter-select >>> .v-input__control {
  background: #f8f9fa;
  border-radius: 12px;
}

.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.action-btn {
  font-weight: 600;
  letter-spacing: 0.5px;
}

/* Table Card */
.table-card {
  background: white;
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.table-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.table-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
  padding: 20px 24px;
}

.title-icon {
  color: #667eea;
  margin-right: 8px;
}

.admin-count-chip {
  font-weight: 600;
}

/* Table Styles */
.modern-table >>> .v-data-table__wrapper {
  border-radius: 0;
}

.modern-table >>> thead tr th {
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #495057;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  height: 56px;
}

.modern-table >>> tbody tr {
  transition: all 0.2s ease;
}

.modern-table >>> tbody tr:hover {
  background: #f8f9fa;
  transform: scale(1.001);
}

.modern-table >>> tbody tr td {
  padding: 16px;
  border-bottom: 1px solid #f1f3f4;
}

/* Avatar */
.avatar-container {
  position: relative;
}

.admin-avatar {
  border: 3px solid #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.avatar-image {
  transition: all 0.3s ease;
}

.avatar-placeholder {
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.status-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid white;
}

.status-indicator.active {
  background: #4CAF50;
}

.status-indicator.inactive {
  background: #9E9E9E;
}

.status-indicator.pending {
  background: #FF9800;
}

/* Name Cell */
.name-cell {
  line-height: 1.3;
}

.admin-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
}

.admin-role {
  color: #6c757d;
  font-size: 0.8rem;
  margin-top: 2px;
}

/* Contact Cell */
.contact-cell {
  font-size: 0.875rem;
}

.contact-item {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
}

.contact-icon {
  color: #6c757d;
  margin-right: 8px;
}

.contact-text {
  color: #495057;
}

/* Status Chip */
.status-chip {
  font-weight: 600;
  font-size: 0.75rem;
  height: 28px;
}

/* Action Buttons */
.action-buttons-cell {
  display: flex;
  gap: 4px;
  justify-content: center;
}

.action-btn-table {
  width: 36px;
  height: 36px;
  transition: all 0.2s ease;
}

.edit-btn:hover {
  background: #e3f2fd;
  color: #1976d2;
}

.delete-btn:hover {
  background: #ffebee;
  color: #d32f2f;
}

.more-btn:hover {
  background: #f5f5f5;
  color: #424242;
}

/* Loading */
.loading-container {
  text-align: center;
  padding: 60px 20px;
}

.loading-text {
  color: #6c757d;
  font-size: 1rem;
}

/* Delete Dialog */
.delete-dialog {
  border-radius: 20px;
  overflow: hidden;
}

.delete-header {
  text-align: center;
  padding: 40px 24px 0;
  background: linear-gradient(135deg, #fef7f7 0%, #fdeaea 100%);
}

.delete-icon-wrapper {
  margin-bottom: 16px;
}

.delete-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #d32f2f;
  margin-bottom: 8px;
}

.delete-subtitle {
  color: #757575;
  font-size: 0.9rem;
  margin: 0;
}

.delete-content {
  padding: 24px;
  text-align: center;
}

.delete-message {
  color: #424242;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
}

.delete-actions {
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
}

.cancel-btn {
  font-weight: 600;
  color: #6c757d;
}

.delete-confirm-btn {
  font-weight: 600;
  color: white;
}

/* Animations */
@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 1264px) {
  .content-container {
    max-width: 100%;
    padding: 0 16px;
  }
}

@media (max-width: 960px) {
  .hero-section {
    padding: 80px 0 120px;
  }
  
  .hero-text h1 {
    font-size: 2.5rem;
  }
  
  .action-buttons {
    justify-content: center;
    margin-top: 16px;
  }
  
  .search-row {
    flex-direction: column;
  }
  
  .action-buttons {
    justify-content: flex-start;
  }
  
  .stats-section {
    margin-top: -20px;
  }
  
  .stat-card {
    margin-bottom: 16px;
  }
}

@media (max-width: 600px) {
  .hero-section {
    padding: 60px 0 100px;
  }
  
  .hero-text {
    text-align: center;
  }
  
  .hero-text h1 {
    font-size: 2rem;
  }
  
  .hero-subtitle {
    font-size: 1rem;
  }
  
  .create-btn {
    width: 100%;
    margin-top: 24px;
  }
  
  .content-container {
    margin-top: -60px;
    padding: 0 12px;
  }
  
  .stat-icon-wrapper {
    width: 48px;
    height: 48px;
  }
  
  .stat-icon {
    font-size: 24px !important;
  }
  
  .stat-number {
    font-size: 1.5rem;
  }
  
  .table-card {
    border-radius: 16px;
  }
  
  .search-card {
    border-radius: 16px;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 8px;
  }
  
  .action-btn {
    width: 100%;
  }
  
  .modern-table >>> tbody tr td {
    padding: 12px 8px;
    font-size: 0.85rem;
  }
  
  .admin-avatar {
    width: 40px !important;
    height: 40px !important;
  }
  
  .action-buttons-cell {
    flex-direction: column;
    gap: 2px;
  }
  
  .action-btn-table {
    width: 32px;
    height: 32px;
  }
  
  .delete-dialog {
    margin: 12px;
    border-radius: 16px;
  }
  
  .delete-header {
    padding: 24px 16px 0;
  }
  
  .delete-title {
    font-size: 1.25rem;
  }
  
  .delete-content {
    padding: 16px;
  }
  
  .delete-actions {
    padding: 16px;
    flex-direction: column;
    gap: 12px;
  }
  
  .cancel-btn,
  .delete-confirm-btn {
    width: 100%;
    margin: 0;
  }
}

@media (max-width: 400px) {
  .hero-text h1 {
    font-size: 1.75rem;
  }
  
  .hero-subtitle {
    font-size: 0.9rem;
  }
  
  .content-container {
    padding: 0 8px;
  }
  
  .stat-card .v-card-text {
    padding: 16px;
  }
  
  .search-card .v-card-text {
    padding: 16px;
  }
  
  .table-title {
    padding: 16px;
    font-size: 1.1rem;
  }
  
  .modern-table >>> thead tr th {
    font-size: 0.75rem;
    height: 48px;
    padding: 0 4px;
  }
  
  .modern-table >>> tbody tr td {
    padding: 8px 4px;
    font-size: 0.8rem;
  }
  
  .contact-text {
    font-size: 0.75rem;
  }
  
  .admin-name {
    font-size: 0.85rem;
  }
  
  .admin-role {
    font-size: 0.7rem;
  }
}

/* Dark theme support */
.theme--dark .admin-container {
  background: linear-gradient(135deg, #263238 0%, #37474f 100%);
}

.theme--dark .hero-background {
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%);
}

.theme--dark .stat-card {
  background: #424242;
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .search-card,
.theme--dark .table-card {
  background: #424242;
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .table-header {
  background: #373737;
}

.theme--dark .modern-table >>> thead tr th {
  background: #373737;
  border-bottom-color: rgba(255, 255, 255, 0.12);
  color: #e0e0e0;
}

.theme--dark .modern-table >>> tbody tr:hover {
  background: #484848;
}

.theme--dark .modern-table >>> tbody tr td {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .admin-name {
  color: #ffffff;
}

.theme--dark .contact-text {
  color: #e0e0e0;
}

.theme--dark .stat-number {
  color: #ffffff;
}

.theme--dark .table-title {
  color: #ffffff;
}

.theme--dark .delete-header {
  background: linear-gradient(135deg, #4a2c2a 0%, #5d4037 100%);
}

.theme--dark .delete-actions {
  background: #373737;
  border-top-color: rgba(255, 255, 255, 0.12);
}

/* Print styles */
@media print {
  .hero-section,
  .search-card,
  .action-buttons,
  .action-buttons-cell,
  .v-data-footer {
    display: none !important;
  }
  
  .admin-container {
    background: white !important;
  }
  
  .table-card,
  .stat-card {
    box-shadow: none !important;
    border: 1px solid #ddd !important;
  }
  
  .modern-table >>> tbody tr:hover {
    background: transparent !important;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .stat-card,
  .search-card,
  .table-card {
    border: 2px solid #000;
  }
  
  .status-chip {
    border: 1px solid #000;
  }
  
  .admin-avatar {
    border-color: #000;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .stat-card,
  .create-btn,
  .action-btn-table,
  .modern-table >>> tbody tr {
    transition: none;
  }
  
  .hero-text,
  .create-btn {
    animation: none;
  }
  
  .hero-icon {
    animation: none;
  }
  
  .stat-card:hover {
    transform: none;
  }
}

/* Focus styles for accessibility */
.create-btn:focus,
.action-btn:focus,
.action-btn-table:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

.search-field >>> .v-input__control:focus-within,
.filter-select >>> .v-input__control:focus-within {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

/* Loading state enhancements */
.modern-table[loading] >>> tbody tr {
  opacity: 0.5;
  pointer-events: none;
}

/* Enhanced status indicators */
.status-indicator {
  animation: pulse 2s infinite;
}

.status-indicator.inactive {
  animation: none;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 4px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

/* Custom scrollbar */
.modern-table >>> .v-data-table__wrapper::-webkit-scrollbar {
  height: 8px;
}

.modern-table >>> .v-data-table__wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.modern-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.modern-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.top-title {
  color: #ffff !important;
}

.top-sub-title {
  color: #ffff !important;
}
</style>